import { useRef, useState } from "react";
import { useEffect } from "react";

import { motion } from "framer-motion";
import { Award, Check, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { Loader } from "../../common/loader";
import AlertDialog from "../../common/AlertDialog";
import {
  getObjLength,
  buildAdminPropertyCreatePayloadFromStored,
  persistStoredPromoCode,
  clearStoredPromoCode,
  encryptPayload,
  normalizeSubscriptionCategory,
  resolveEnhancedMonitoringReportIds,
} from "../../helpers/utils";
import {
  canSelectEnhancedMonitoring,
  getEnhancedMonitoringUnavailableMessage,
  getPropertySearchMode,
} from "../../helpers/enhancedMonitoringSearchRules";
import {
  getComplimentaryEnhancedNote,
  isEldoradoComplimentaryEnhancedEligible,
  setEldoradoAdminEnhancedBillingChoice,
  getEldoradoAdminEnhancedBillingChoice,
  complimentaryEnhancedFreeFlagPayload,
  buildComplimentaryEnhancedMonitoringPayload,
  isLoggedInSuperOrInternalAdmin,
} from "../../helpers/eldoradoSignup";
import {
  attachEnhancedToCompleteUpgradeFlags,
  generateCustomerAddPropertyCompleteProtectionQuestions,
  hasPropertyGeneratedQuestions,
} from "../../helpers/generatePropertySecurityQuestions";
import { Button } from "../../common/customDesignedComponents/button";
import { mapCreateUserApiUrl } from "../../for-service-provider/createUserApiPaths";
import { postAPIPayload } from "../../../apis/postApi";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";
import { propertiesAction } from "../../../redux/slices/properties/propertiesSlice";
import OrderSummaryModal, {
  hasAppliedPromoDiscount,
  isValidPaymentSummaryData,
} from "../../common/customDesignedComponents/OrderSummaryModal";
import { BasicNotificationDisclaimerModal } from "../../common/customDesignedComponents/BasicNotificationDisclaimerModal";

type GetFinalAmountPayload = {
  plan_id: number;
  user_id: any;
  property_id: any[];
  property_ids: {
    address: any;
    property_id: any;
    city: any;
    state: any;
    zip: any;
  }[];
  promo_code?: string | null;
  upgrade_from_enhanced?: boolean;
  risk_report_id?: number | string;
};

const CategoryWisePricing = ({
  onChangeModalScreen,
  setSelectedCategory,
  selectedCategory = null,
  setPlanId = () => {},
  handleCloseAddPropertyModal,
  from = "",
  setFinalAmount = () => {},
  isUserCreateProperty = false,
  uid = null,
  selectedData = {},
  //for add-subscriptions
  setSelectdPlan = () => {},
  customClassName = "",
  setPlanSelected = () => {},
  setAddSubscriptionPaymentCategory = () => {},
  isAdmin = false,
  isTailwindComponent = false,
  disableBasicMonitoring = false,
  isUpgradeCategory = false,
  onGenerateSecurityQuestions = () => {},
  subscriptionbyAdmin = false,
  showAllSubscriptionCategories = false,
  isServiceProvider = false,
  isAdminCreateUser = false,
  workspaceUser = null,
  completeProtectionOnly = false,
}: {
  onChangeModalScreen: (screen: string) => void;
  setSelectedCategory: (category: string) => void;
  setPlanId?: (planId: string) => void;
  handleCloseAddPropertyModal: (type?: string) => void;
  from: string;
  uid: string | null;
  selectedData: any;
  isAssistant: boolean;
  setFinalAmount?: (finalAmount: any) => void;
  isUserCreateProperty: boolean;
  selectedCategory: string | null;
  setSelectdPlan: (plan: any) => void;
  customClassName: string;
  setPlanSelected: (plan: any) => void;
  setAddSubscriptionPaymentCategory?: (category: string) => void;
  isAdmin: boolean;
  isTailwindComponent: boolean;
  disableBasicMonitoring?: boolean;
  isUpgradeCategory?: boolean;
  onGenerateSecurityQuestions?: (property: any) => void;
  subscriptionbyAdmin?: boolean;
  showAllSubscriptionCategories?: boolean;
  isServiceProvider?: boolean;
  isAdminCreateUser?: boolean;
  workspaceUser?: any;
  completeProtectionOnly?: boolean;
}) => {
  const dispatch = useDispatch();
  const {
    statesLoader,
    subscriptionPlans,
    statesCodeList,
    createAccResponse,
    paymentData,
    paymnetLoader,
    property_ids,
    propertyIds,
  } = useSelector((state: any) => state?.signUpUser);
  const {
    basicMonitoringData,
    basicMonitoringLoader,
    genearteQuesPayload,
    selectedProperty,
    selectedUserID,
    propertyQuestion,
    securityPropertiesQueData,
    propertyLoader,
  } = useSelector((state: any) => state?.properties);

  const { totalPaymentDetails, getTotalAmountLoader } = useSelector(
    (state: any) => state?.signUpUser,
  );

  const userProfileDetails = useSelector(
    (state: any) => state?.userProfile?.userProfileDetails,
  );

  const [showTotalModal, setShowTotalModal] = useState(false);
  const [showPaymentError, setShowPaymentError] = useState(false);
  const [showBasicDisclaimerModal, setShowBasicDisclaimerModal] =
    useState(false);
  const [selectedPlanForModal, setSelectedPlanForModal] = useState<
    string | null
  >(null);
  const [pendingUpgradePlan, setPendingUpgradePlan] = useState<string | null>(
    null,
  );
  const [pendingCustomerAddPropertyQuestions, setPendingCustomerAddPropertyQuestions] =
    useState(false);
  const [showQuestionsError, setShowQuestionsError] = useState(false);
  const [questionsErrorMessage, setQuestionsErrorMessage] = useState("");
  const [
    enhancedMonitoringBlockedMessage,
    setEnhancedMonitoringBlockedMessage,
  ] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [promoFeedback, setPromoFeedback] = useState({
    message: "",
    isError: false,
  });
  const [cachedPaymentData, setCachedPaymentData] = useState<any>(null);
  const [finalAmountRequest, setFinalAmountRequest] = useState<{
    url: string;
    data: GetFinalAmountPayload;
    token?: boolean;
  } | null>(null);
  const [complimentaryEnrollResult, setComplimentaryEnrollResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [complimentaryReportLoader, setComplimentaryReportLoader] =
    useState(false);
  const promoApplyRef = useRef(false);
  const pendingPromoRef = useRef("");
  const pendingComplimentaryEnhancedRef = useRef(false);
  const confirmingOrderSummaryRef = useRef(false);

  const property = getObjLength(selectedProperty)
    ? selectedProperty
    : selectedData;

  const selectCategoryThenSearch =
    isAdmin || (isServiceProvider && isAdminCreateUser);
  // A CAR agent buying for a client sells only Complete Protection, so the
  // picker is skipped (see the effect before render). The opening page passes
  // the flag; it already knows whether the agent is a CAR agent.
  const skipCategoryPicker = Boolean(
    completeProtectionOnly && isServiceProvider && isAdminCreateUser,
  );
  const autoSelectedRef = useRef(false);
  const isCustomerPropertiesAddProperty =
    !selectCategoryThenSearch &&
    !isUpgradeCategory &&
    isUserCreateProperty &&
    from !== "add-subscriptions";
  const isAdminDualEnhancedPricing =
    selectCategoryThenSearch || subscriptionbyAdmin;
  // Complimentary Enhanced: Add Property for admin dual pricing, plus Admin
  // Add Subscription (Super Admin / Internal Admin only — not SP).
  const showAdminComplimentaryEnhancedOption =
    isAdminDualEnhancedPricing &&
    (from !== "add-subscriptions" ||
      isLoggedInSuperOrInternalAdmin(userProfileDetails));
  const isEldoradoComplimentaryEnhanced =
    !isAdminDualEnhancedPricing &&
    !selectCategoryThenSearch &&
    !subscriptionbyAdmin &&
    isEldoradoComplimentaryEnhancedEligible(
      userProfileDetails?.data || userProfileDetails,
      property,
    );
  const complimentaryEnhancedNote = getComplimentaryEnhancedNote(
    userProfileDetails?.data || userProfileDetails,
  );
  const enhancedMonitoringUnavailableForProperty =
    !selectCategoryThenSearch &&
    !isEldoradoComplimentaryEnhanced &&
    !canSelectEnhancedMonitoring({
      searchMode: getPropertySearchMode(),
      property,
      profile: userProfileDetails,
    });
  const enhancedMonitoringUnavailableMessage =
    getEnhancedMonitoringUnavailableMessage();

  const propertySubscriptionCategory = normalizeSubscriptionCategory(property);

  const shouldShowPlan = (planTier: string) => {
    if (isAdminDualEnhancedPricing) {
      return true;
    }
    // El Dorado complimentary (customer self-service): hide Basic
    if (isEldoradoComplimentaryEnhanced && planTier === "basic_monitoring") {
      return false;
    }
    // Add Subscription: only show the category this property was added with
    if (from === "add-subscriptions") {
      if (propertySubscriptionCategory && !showAllSubscriptionCategories) {
        return propertySubscriptionCategory === planTier;
      }
      if (selectedCategory && !showAllSubscriptionCategories) {
        return selectedCategory === planTier;
      }
      return true;
    }
    if (!subscriptionbyAdmin || showAllSubscriptionCategories) {
      return true;
    }
    return selectedCategory === planTier;
  };

  const restrictToSingleCategory =
    !isAdminDualEnhancedPricing &&
    ((from === "add-subscriptions" &&
      Boolean(propertySubscriptionCategory || selectedCategory)) ||
      (subscriptionbyAdmin && !showAllSubscriptionCategories));

  const visiblePlanCount =
    Number(shouldShowPlan("basic_monitoring")) +
    Number(shouldShowPlan("enhanced_monitoring")) +
    Number(shouldShowPlan("complete_protection"));

  const hasGeneratedQuestions = () =>
    hasPropertyGeneratedQuestions(propertyQuestion, property);

  const resolveComplimentaryEnhancedUserId = () => {
    const createdUserId = getObjLength(createAccResponse)
      ? createAccResponse?.data?.user_id
      : null;
    try {
      const raw = localStorage.getItem("user_Id");
      const parsed = raw ? JSON.parse(raw) : null;
      return (
        selectedUserID ||
        uid ||
        createdUserId ||
        userProfileDetails?.data?.user_id ||
        userProfileDetails?.user_id ||
        property?.owner_id ||
        property?.user_id ||
        parsed ||
        localStorage.getItem("uID") ||
        null
      );
    } catch {
      return (
        selectedUserID ||
        uid ||
        createdUserId ||
        userProfileDetails?.data?.user_id ||
        userProfileDetails?.user_id ||
        property?.owner_id ||
        property?.user_id ||
        localStorage.getItem("uID") ||
        null
      );
    }
  };

  const resolveComplimentaryEnhancedReportIds = () => {
    const fromSignupState = resolveEnhancedMonitoringReportIds({
      property_ids,
      propertyIds,
    });
    const fromSignupList = (
      Array.isArray(fromSignupState)
        ? fromSignupState
        : fromSignupState
          ? [fromSignupState]
          : []
    )
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (fromSignupList.length) {
      return fromSignupList;
    }

    const candidates = [
      propertyQuestion?.data?.risk_report_id,
      propertyQuestion?.data?.id,
      property?.risk_report_id,
      property?.report_id,
      property?.subscription?.risk_report_id,
      property?.id,
    ];
    try {
      const raw = localStorage.getItem("property_search_data");
      const searchData = raw ? JSON.parse(raw) : null;
      candidates.unshift(
        searchData?.risk_report_id,
        searchData?.user_data?.risk_report_id,
        searchData?.id,
      );
      candidates.push(
        searchData?.property_id,
        searchData?.user_data?.property_id,
      );
    } catch {
      // ignore invalid property_search_data
    }

    return Array.from(
      new Set(
        candidates
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    );
  };

  const enrollComplimentaryEnhancedMonitoring = async () => {
    const find_plan = subscriptionPlans?.data?.find(
      (data: any) => data?.product_tier === "enhanced_monitoring",
    );
    if (find_plan) {
      setSelectdPlan(find_plan);
      setSelectedCategory(find_plan?.product_tier);
      setPlanId(find_plan?.plan_id);
    } else {
      setSelectedCategory("enhanced_monitoring");
    }
    localStorage.setItem("add_subscription_category", "enhanced_monitoring");
    localStorage.setItem("category", encryptPayload("enhanced_monitoring"));

    const userId = resolveComplimentaryEnhancedUserId();
    if (!userId) {
      setComplimentaryEnrollResult({
        success: false,
        message:
          "Unable to activate Complimentary Enhanced Monitoring for this property. Please try again.",
      });
      return;
    }

    // Same API as signup complimentary Enhanced — no Stripe token/card_id.
    setComplimentaryEnrollResult(null);
    dispatch(userSigupAction.fetchPaymentData({}));
    pendingComplimentaryEnhancedRef.current = true;

    let riskReportIds = resolveComplimentaryEnhancedReportIds();

    // Prefer the Add Subscription property (selectedData) over Redux selectedProperty,
    // which can be a stale workspace row from a different flow.
    const reportSource =
      from === "add-subscriptions" && getObjLength(selectedData)
        ? selectedData
        : property || selectedData || {};
    // normalizeSubscriptionCategory expects a property object (not a bare string).
    const existingPropertyCategory =
      normalizeSubscriptionCategory(reportSource) ||
      propertySubscriptionCategory;

    // Create the Enhanced risk report first when there isn't one to enroll: a
    // Complete Protection property being moved over, or a brand-new property
    // (e.g. a county-site owner adding another property) that no earlier step
    // has reported on yet.
    if (
      getObjLength(reportSource) &&
      (existingPropertyCategory === "complete_protection" ||
        !riskReportIds.length)
    ) {
      setComplimentaryReportLoader(true);
      try {
        const reportPayload = {
          ...buildAdminPropertyCreatePayloadFromStored(
            reportSource,
            userId,
            {
              statesCodeList,
              extra: {
                category: "enhanced_monitoring",
                step: 2,
                step_key: "GET_MAILING_ADDRESS",
              },
            },
          ),
          category: "enhanced_monitoring",
          user_id: Number(userId),
        };
        const reportResponse = await postAPIPayload({
          url: "properties/add-property-in-report",
          data: reportPayload,
          token: true,
        });

        if (!reportResponse?.success) {
          pendingComplimentaryEnhancedRef.current = false;
          setComplimentaryEnrollResult({
            success: false,
            message:
              reportResponse?.message ||
              reportResponse?.data?.message ||
              "Unable to create a risk report for Complimentary Enhanced Monitoring. Please try again.",
          });
          return;
        }

        const paymentPayload = buildComplimentaryEnhancedMonitoringPayload(
          reportResponse,
          userId,
          {
            isAdminComplimentary: isLoggedInSuperOrInternalAdmin(
              userProfileDetails,
            ),
          },
        );
        if (paymentPayload) {
          dispatch(
            userSigupAction.getPayment({
              url: "payment-enhanced-monitoring",
              data: paymentPayload,
              token: true,
            }),
          );
          return;
        }

        const reportId =
          reportResponse?.data?.id ??
          reportResponse?.data?.risk_report_id ??
          reportResponse?.data?.report_id;
        riskReportIds = [Number(reportId)].filter(
          (id) => Number.isFinite(id) && id > 0,
        );
        if (!riskReportIds.length) {
          pendingComplimentaryEnhancedRef.current = false;
          setComplimentaryEnrollResult({
            success: false,
            message:
              "Unable to activate Complimentary Enhanced Monitoring for this property. Please try again.",
          });
          return;
        }
      } catch (error: any) {
        pendingComplimentaryEnhancedRef.current = false;
        setComplimentaryEnrollResult({
          success: false,
          message:
            error?.message ||
            "Unable to create a risk report for Complimentary Enhanced Monitoring. Please try again.",
        });
        return;
      } finally {
        setComplimentaryReportLoader(false);
      }
    }

    if (!riskReportIds.length) {
      pendingComplimentaryEnhancedRef.current = false;
      setComplimentaryEnrollResult({
        success: false,
        message:
          "Unable to activate Complimentary Enhanced Monitoring for this property. Please try again.",
      });
      return;
    }

    dispatch(
      userSigupAction.getPayment({
        url: "payment-enhanced-monitoring",
        data: {
          user_id: userId,
          risk_report_id: riskReportIds,
          ...complimentaryEnhancedFreeFlagPayload(
            isLoggedInSuperOrInternalAdmin(userProfileDetails),
          ),
        },
        token: true,
      }),
    );
  };

  const fetchFinalAmountForPlan = (plan: string) => {
    const find_plan = subscriptionPlans?.data?.find(
      (data: any) => data?.product_tier === plan,
    );
    if (!find_plan) return;

    setSelectdPlan(find_plan);
    setSelectedCategory(find_plan?.product_tier);
    if (from === "add-subscriptions") {
      setAddSubscriptionPaymentCategory(plan);
    }
    localStorage.setItem("add_subscription_category", plan);
    localStorage.setItem("category", encryptPayload(plan));
    setPlanId(find_plan?.plan_id);

    let pc = localStorage.getItem("promoCode");
    // Prefer the selected/client user (admin/SP create & resume payment) over
    // the logged-in actor's profile id, which is often null for servicers.
    const createdUserId = getObjLength(createAccResponse)
      ? createAccResponse?.data?.user_id
      : null;
    let user_id =
      selectedUserID ||
      uid ||
      createdUserId ||
      userProfileDetails?.data?.user_id ||
      userProfileDetails?.user_id;
    if (from === "add-subscriptions") {
      // Keep uid first, but fall back so SP personal (no profile.data.user_id) still works
      user_id =
        uid ||
        selectedUserID ||
        createdUserId ||
        userProfileDetails?.data?.user_id ||
        userProfileDetails?.user_id ||
        property?.owner_id ||
        property?.user_id ||
        (() => {
          try {
            const raw = localStorage.getItem("user_Id");
            if (!raw) return localStorage.getItem("uID") || null;
            const parsed = JSON.parse(raw);
            return parsed || localStorage.getItem("uID") || null;
          } catch {
            return localStorage.getItem("uID") || null;
          }
        })();
    }
    let data: GetFinalAmountPayload = {
      plan_id: parseInt(find_plan?.plan_id),
      user_id: user_id,
      property_id: [selectedProperty?.property_id],
      property_ids: [
        {
          address: selectedProperty?.address,
          property_id: selectedProperty?.property_id,
          city: selectedProperty?.city,
          state: selectedProperty?.state,
          zip: selectedProperty?.zip,
        },
      ],
    };

    if (from === "add-subscriptions") {
      data = {
        plan_id: parseInt(find_plan?.plan_id),
        user_id: user_id,
        property_id: [selectedData?.property_id],
        property_ids: [
          {
            address: selectedData?.address,
            property_id: selectedData?.property_id,
            city: selectedData?.city,
            state: selectedData?.state,
            zip: selectedData?.zip,
          },
        ],
      };
    }
    if (pc?.length > 0) {
      data.promo_code = pc;
    }

    let url = "customer/get-final-amount";
    if (plan === "enhanced_monitoring") {
      url = `get-final-amount-enhanced-monitoring`;
    } else {
      url = mapCreateUserApiUrl(url, isServiceProvider);
    }
    if (isUserCreateProperty) {
      data = {
        plan_id: parseInt(find_plan?.plan_id),
        user_id: user_id,
        property_id: [selectedProperty?.property_id],
        property_ids: [
          {
            address: selectedProperty?.address,
            property_id: selectedProperty?.property_id,
            state: selectedProperty?.state,
            city: selectedProperty?.city,
            zip: selectedProperty?.zip,
          },
        ],
      };
    } else if (localStorage.getItem("promoCode")) {
      data = {
        ...data,
        promo_code: localStorage.getItem("promoCode"),
      };
    }

    if (plan === "complete_protection") {
      const upgradeProperty =
        from === "add-subscriptions"
          ? selectedData || property || selectedProperty
          : property || selectedProperty || selectedData;
      data = attachEnhancedToCompleteUpgradeFlags(data, {
        property: upgradeProperty,
        propertyQuestion,
      }) as GetFinalAmountPayload;
    }

    setSelectedPlanForModal(plan);
    promoApplyRef.current = false;
    const request = {
      url,
      data,
      ...(isServiceProvider ? { token: true } : {}),
    };
    setFinalAmountRequest(request);
    dispatch(userSigupAction.getTotalPaymentDetails(request));

    localStorage.setItem(
      "Plan_Data",
      JSON.stringify({ ...data, state: selectedProperty?.state }),
    );
    localStorage.setItem("Plan_Name", JSON.stringify(find_plan?.name));
  };

  const handleApplyPromoCode = () => {
    const code = promoInput.trim();
    if (!code || !finalAmountRequest) {
      return;
    }

    promoApplyRef.current = true;
    pendingPromoRef.current = code;
    setPromoFeedback({ message: "", isError: false });

    const nextRequest = {
      url: finalAmountRequest.url,
      data: {
        ...finalAmountRequest.data,
        promo_code: code,
      },
      ...(isServiceProvider || finalAmountRequest.token ? { token: true } : {}),
    };
    setFinalAmountRequest(nextRequest);

    dispatch(userSigupAction.getTotalPaymentDetails(nextRequest));
  };

  const clearOrderSummaryPromo = () => {
    clearStoredPromoCode();
    pendingPromoRef.current = "";
    setPromoInput("");
    setPromoFeedback({ message: "", isError: false });
    setFinalAmountRequest((current) => {
      if (!current?.data) {
        return current;
      }
      const { promo_code: _removed, ...restData } = current.data;
      return { ...current, data: restData };
    });
  };

  const handleRemovePromoCode = () => {
    if (!finalAmountRequest?.data) {
      clearOrderSummaryPromo();
      return;
    }
    const { promo_code: _removed, ...restData } = finalAmountRequest.data;
    clearStoredPromoCode();
    pendingPromoRef.current = "";
    setPromoInput("");
    setPromoFeedback({ message: "", isError: false });
    const nextRequest: {
      url: string;
      data: GetFinalAmountPayload;
      token?: boolean;
    } = {
      url: finalAmountRequest.url,
      data: restData,
      ...(isServiceProvider || finalAmountRequest.token ? { token: true } : {}),
    };
    setFinalAmountRequest(nextRequest);
    dispatch(userSigupAction.getTotalPaymentDetails(nextRequest));
  };

  const handleSelectEnhancedMonitoring = (
    billingMode: "complimentary" | "regular",
  ) => {
    setEnhancedMonitoringBlockedMessage("");

    if (billingMode === "complimentary") {
      setEldoradoAdminEnhancedBillingChoice("complimentary");
    } else {
      setEldoradoAdminEnhancedBillingChoice("regular");
    }

    if (enhancedMonitoringUnavailableForProperty) {
      setEnhancedMonitoringBlockedMessage(enhancedMonitoringUnavailableMessage);
      return;
    }

    if (selectCategoryThenSearch) {
      setSelectedCategory("enhanced_monitoring");
      localStorage.setItem("add_subscription_category", "enhanced_monitoring");
      localStorage.setItem("category", encryptPayload("enhanced_monitoring"));
      onChangeModalScreen("security");
      return;
    }

    if (billingMode === "complimentary") {
      enrollComplimentaryEnhancedMonitoring();
      return;
    }

    fetchFinalAmountForPlan("enhanced_monitoring");
  };

  const handleSelectPlan = (plan: any) => {
    // Admin workspace + SP adding for a client: pick category, then search
    // property. Do not call get-final-amount until plans/payment later.
    if (selectCategoryThenSearch) {
      setEnhancedMonitoringBlockedMessage("");
      setSelectedCategory(plan);
      localStorage.setItem("add_subscription_category", plan);
      localStorage.setItem("category", encryptPayload(plan));
      onChangeModalScreen("security");
      return;
    }

    if (
      plan === "enhanced_monitoring" &&
      enhancedMonitoringUnavailableForProperty
    ) {
      setEnhancedMonitoringBlockedMessage(enhancedMonitoringUnavailableMessage);
      return;
    }

    setEnhancedMonitoringBlockedMessage("");

    if (plan === "enhanced_monitoring" && isEldoradoComplimentaryEnhanced) {
      enrollComplimentaryEnhancedMonitoring();
      return;
    }

    if (plan === "basic_monitoring") {
      let basicMonitoringUrl = "customer/add-basic-monitoring-property";
      let payloadData: any = {
        ...genearteQuesPayload,
        source: "equityprotect",
        step_key: "GET_MAILING_ADDRESS",
      };

      if (subscriptionbyAdmin) {
        basicMonitoringUrl = "admin/add-basic-monitoring-property";
        const resolvedUserId = selectedUserID || uid;
        payloadData = buildAdminPropertyCreatePayloadFromStored(
          selectedData,
          resolvedUserId,
          {
            statesCodeList,
            extra: {
              step: 2,
              step_key: "GET_MAILING_ADDRESS",
            },
          },
        );
      }

      if (propertyQuestion?.data?.id) {
        payloadData.report_id = propertyQuestion.data.id;
      }
      dispatch(
        propertiesAction.getBasicMonitoringData({
          url: basicMonitoringUrl,
          data: payloadData,
          type: "post",
          token: true,
        }),
      );
      return;
    }

    if (plan === "complete_protection" && isUpgradeCategory) {
      const find_plan = subscriptionPlans?.data?.find(
        (data: any) => data?.product_tier === plan,
      );
      if (!find_plan) return;

      setSelectdPlan(find_plan);
      setSelectedCategory(find_plan?.product_tier);
      localStorage.setItem("add_subscription_category", "complete_protection");
      setPlanId(find_plan?.plan_id);

      if (hasGeneratedQuestions()) {
        fetchFinalAmountForPlan("complete_protection");
        return;
      }

      if (property?.id) {
        setPendingUpgradePlan("complete_protection");
        onGenerateSecurityQuestions?.(property);
      }
      return;
    }

    if (plan === "complete_protection" && isCustomerPropertiesAddProperty) {
      const find_plan = subscriptionPlans?.data?.find(
        (data: any) => data?.product_tier === plan,
      );
      if (!find_plan) return;

      setSelectdPlan(find_plan);
      setSelectedCategory(find_plan?.product_tier);
      localStorage.setItem("add_subscription_category", "complete_protection");
      localStorage.setItem("category", encryptPayload("complete_protection"));
      setPlanId(find_plan?.plan_id);
      fetchFinalAmountForPlan("complete_protection");
      return;
    }

    fetchFinalAmountForPlan(plan);
  };

  useEffect(() => {
    if (pendingUpgradePlan !== "complete_protection") return;
    if (!getObjLength(securityPropertiesQueData)) return;

    if (!securityPropertiesQueData?.success) {
      setQuestionsErrorMessage(
        securityPropertiesQueData?.data?.message ||
          securityPropertiesQueData?.message ||
          "Failed to generate security questions.",
      );
      setShowQuestionsError(true);
      setPendingUpgradePlan(null);
      return;
    }

    dispatch(
      propertiesAction.setGeneratedQuestionsData(securityPropertiesQueData),
    );
    fetchFinalAmountForPlan("complete_protection");
    setPendingUpgradePlan(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityPropertiesQueData, pendingUpgradePlan]);

  useEffect(() => {
    if (!pendingCustomerAddPropertyQuestions) return;
    if (!getObjLength(securityPropertiesQueData)) return;

    if (!securityPropertiesQueData?.success) {
      setQuestionsErrorMessage(
        securityPropertiesQueData?.data?.message ||
          securityPropertiesQueData?.message ||
          "Failed to generate security questions.",
      );
      setShowQuestionsError(true);
      setPendingCustomerAddPropertyQuestions(false);
      return;
    }

    dispatch(
      propertiesAction.setGeneratedQuestionsData(securityPropertiesQueData),
    );
    setPendingCustomerAddPropertyQuestions(false);
    onChangeModalScreen("questions");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityPropertiesQueData, pendingCustomerAddPropertyQuestions]);

  const closeQuestionsError = () => {
    setShowQuestionsError(false);
    setQuestionsErrorMessage("");
  };

  useEffect(() => {
    if (!getObjLength(totalPaymentDetails) || !selectedPlanForModal) return;

    if (totalPaymentDetails?.success) {
      const nextData = totalPaymentDetails?.data;
      if (promoApplyRef.current) {
        const appliedCode = pendingPromoRef.current.trim();
        const discountApplied = hasAppliedPromoDiscount(nextData);
        if (
          appliedCode &&
          discountApplied &&
          isValidPaymentSummaryData(nextData)
        ) {
          setCachedPaymentData(nextData);
          persistStoredPromoCode(appliedCode);
          setPromoFeedback({ message: "Promo code applied.", isError: false });
        } else {
          // Keep existing pricing breakdown; show error under promo field only
          clearStoredPromoCode();
          setFinalAmountRequest((current) => {
            if (!current?.data) {
              return current;
            }
            const { promo_code: _removed, ...restData } = current.data;
            return { ...current, data: restData };
          });
          setPromoFeedback({
            message:
              totalPaymentDetails?.data?.message ||
              totalPaymentDetails?.message ||
              "Unable to apply promo code.",
            isError: true,
          });
        }
        promoApplyRef.current = false;
        pendingPromoRef.current = "";
        setShowPaymentError(false);
        setShowTotalModal(true);
        return;
      }
      if (isValidPaymentSummaryData(nextData)) {
        setCachedPaymentData(nextData);
      }
      setShowPaymentError(false);
      setShowTotalModal(true);
      return;
    }

    if (promoApplyRef.current) {
      // Keep Order Summary open — show wrong-promo error under the promo field only
      clearStoredPromoCode();
      pendingPromoRef.current = "";
      setFinalAmountRequest((current) => {
        if (!current?.data) {
          return current;
        }
        const { promo_code: _removed, ...restData } = current.data;
        return { ...current, data: restData };
      });
      setPromoFeedback({
        message:
          totalPaymentDetails?.data?.message ||
          totalPaymentDetails?.message ||
          "Unable to apply promo code.",
        isError: true,
      });
      promoApplyRef.current = false;
      return;
    }

    if (finalAmountRequest?.data?.promo_code) {
      clearStoredPromoCode();
      setFinalAmountRequest((current) => {
        if (!current?.data) {
          return current;
        }
        const { promo_code: _removed, ...restData } = current.data;
        return { ...current, data: restData };
      });
      // Keep order summary open; only strip the bad stored promo.
      return;
    }

    // Add Subscription: the plan was refused (e.g. property already on another account).
    if (from === "add-subscriptions") {
      setShowPaymentError(true);
    }
    setShowTotalModal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-running when we strip promo_code from finalAmountRequest
  }, [totalPaymentDetails, selectedPlanForModal]);

  useEffect(() => {
    if (!showTotalModal) {
      return;
    }
    const initialPromo =
      finalAmountRequest?.data?.promo_code ||
      localStorage.getItem("promoCode") ||
      "";
    setPromoInput(initialPromo);
    setPromoFeedback({ message: "", isError: false });
    // Only re-init when the modal opens — not when finalAmountRequest is stripped after a bad promo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTotalModal]);

  const closePaymentError = () => {
    setShowPaymentError(false);
    setShowTotalModal(false);
    setSelectedPlanForModal(null);
    dispatch(userSigupAction.fetchTotalPaymentDetails({}));
  };

  const handleConfirmOrderSummary = () => {
    const confirmedPlan = selectedPlanForModal;
    const fromApi =
      totalPaymentDetails?.success && getObjLength(totalPaymentDetails?.data)
        ? totalPaymentDetails.data
        : null;
    const confirmedAmount = isValidPaymentSummaryData(fromApi)
      ? fromApi
      : isValidPaymentSummaryData(cachedPaymentData)
        ? cachedPaymentData
        : fromApi || cachedPaymentData;
    confirmingOrderSummaryRef.current = true;
    const promoToCarry =
      promoInput.trim() ||
      finalAmountRequest?.data?.promo_code ||
      localStorage.getItem("promoCode") ||
      "";
    if (
      promoToCarry &&
      (hasAppliedPromoDiscount(confirmedAmount) ||
        Boolean(finalAmountRequest?.data?.promo_code))
    ) {
      persistStoredPromoCode(promoToCarry);
      try {
        sessionStorage.setItem("ep_carried_promo", promoToCarry);
      } catch {
        // ignore
      }
    }
    setFinalAmount(confirmedAmount);
    // Close Order Summary before continuing so any later API error modal cannot stack over it
    setShowTotalModal(false);
    setSelectedPlanForModal(null);
    // Keep totalPaymentDetails for the payment-screen OrderSummaryBreakdown.
    if (from === "add-subscriptions") {
      if (confirmedPlan) {
        setSelectedCategory(confirmedPlan);
        setAddSubscriptionPaymentCategory(confirmedPlan);
        localStorage.setItem("add_subscription_category", confirmedPlan);
      }
      setPlanSelected(true);
      return;
    }
    if (selectedCategory === "enhanced_monitoring") {
      onChangeModalScreen("cardsInfo");
    } else if (selectedCategory === "complete_protection") {
      if (isCustomerPropertiesAddProperty) {
        if (hasGeneratedQuestions()) {
          onChangeModalScreen("questions");
          return;
        }
        const started = generateCustomerAddPropertyCompleteProtectionQuestions(
          dispatch,
          genearteQuesPayload,
          propertyQuestion,
          property,
        );
        if (!started) {
          setQuestionsErrorMessage(
            "We couldn't prepare security questions for this property. Please search again.",
          );
          setShowQuestionsError(true);
          return;
        }
        setPendingCustomerAddPropertyQuestions(true);
        return;
      }
      onChangeModalScreen("questions");
    }
  };

  useEffect(() => {
    dispatch(
      userSigupAction.getSubscriptionPlan({
        url: "get-plans",
        data: {},
        type: "post",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (!pendingComplimentaryEnhancedRef.current) {
      return;
    }
    // Wait for payment-enhanced-monitoring to finish (loader cleared by saga).
    if (paymnetLoader) {
      return;
    }
    if (!getObjLength(paymentData)) {
      return;
    }

    pendingComplimentaryEnhancedRef.current = false;
    if (paymentData?.success) {
      setComplimentaryEnrollResult({
        success: true,
        message:
          paymentData?.message ||
          paymentData?.data?.message ||
          "Complimentary Enhanced Monitoring activated for this El Dorado County property.",
      });
      return;
    }

    setComplimentaryEnrollResult({
      success: false,
      message:
        paymentData?.message ||
        paymentData?.data?.message ||
        "Unable to activate Complimentary Enhanced Monitoring.",
    });
  }, [paymentData, paymnetLoader]);

  useEffect(() => {
    if (basicMonitoringData) {
      console.log("basicMonitoringData==>", basicMonitoringData);
    }
  }, [basicMonitoringData]);

  useEffect(() => {
    if (!skipCategoryPicker || autoSelectedRef.current) return;
    autoSelectedRef.current = true;
    // Same as clicking the Complete Protection card.
    handleSelectPlan("complete_protection");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipCategoryPicker]);

  if (skipCategoryPicker) {
    return null;
  }

  return (
    <div className="tailwind_component">
      <div className="">
        {/* Pricing Cards */}
        <section className={`py-16 bg-muted/30 ${customClassName}`}>
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-4 text-foreground">
                {restrictToSingleCategory
                  ? "Selected Protection Plan"
                  : "Choose Your Level of Protection"}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {restrictToSingleCategory
                  ? isEldoradoComplimentaryEnhanced
                    ? complimentaryEnhancedNote
                    : "Review the plan for this property and continue to payment."
                  : isAdminDualEnhancedPricing
                    ? showAdminComplimentaryEnhancedOption
                      ? "Choose Complimentary or Regular Enhanced Monitoring for this customer."
                      : "Review the plan for this property and continue to payment."
                    : isEldoradoComplimentaryEnhanced
                      ? complimentaryEnhancedNote
                      : "No gimmicks. No hidden fees. Just real protection that works."}
              </p>
            </motion.div>

            <div
              className={`grid gap-6 mx-auto items-start ${
                visiblePlanCount === 1
                  ? "max-w-md"
                  : visiblePlanCount === 2
                    ? "max-w-4xl md:grid-cols-2"
                    : "max-w-6xl md:grid-cols-3"
              }`}
            >
              {/* Reactive - Basic Monitoring */}
              {shouldShowPlan("basic_monitoring") && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-card rounded-2xl shadow-lg overflow-hidden"
                >
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-medium uppercase tracking-wide mb-4 text-green-600">
                      Reactive
                    </h3>
                    <div className="bg-gradient-to-r from-green-600 to-green-700 py-4 -mx-6">
                      <span className="text-white font-semibold text-lg block mb-2">
                        Basic Monitoring
                      </span>
                      <div className="text-white">
                        <span className="text-4xl fw-300">NO COST</span>
                      </div>
                    </div>
                  </div>
                  <div className="px-6 pb-6">
                    <ul className="space-y-4">
                      <li className="py-3 border-b border-border/50 text-foreground">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">
                            County Recorder Property Monitoring
                          </span>
                          <Check className="h-5 w-5 text-secondary shrink-0 ml-2" />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed text-left">
                          Continuously scans official recording data to alert
                          property owners the moment any document is filed
                          against their title.
                        </p>
                      </li>
                      <li className="py-3 border-b border-border/50 text-muted-foreground/50">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">
                            Property Listing Site Monitoring
                          </span>
                          <X className="h-5 w-5 text-red-500 shrink-0 ml-2" />
                        </div>
                      </li>
                      <li className="py-3 border-b border-border/50 text-muted-foreground/50">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">
                            Complete Protection Property Locking
                          </span>
                          <X className="h-5 w-5 text-red-500 shrink-0 ml-2" />
                        </div>
                      </li>
                      <li className="py-3 text-muted-foreground/50">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">$1 Million Guarantee</span>
                          <X className="h-5 w-5 text-red-500 shrink-0 ml-2" />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground/70 leading-relaxed">
                          Not Included in Basic Monitoring
                        </p>
                      </li>
                    </ul>
                    <Button
                      className={`w-full mt-6 bg-green-600 hover:bg-green-700 text-white ${
                        disableBasicMonitoring
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      disabled={disableBasicMonitoring}
                      onClick={() => {
                        if (disableBasicMonitoring) return;
                        setShowBasicDisclaimerModal(true);
                      }}
                    >
                      SELECT
                    </Button>
                    {disableBasicMonitoring && (
                      <p className="text-xs text-muted-foreground text-center mt-2">
                        Current plan
                      </p>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Proactive - Enhanced Monitoring */}
              {shouldShowPlan("enhanced_monitoring") && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="bg-card rounded-2xl shadow-lg overflow-hidden"
                >
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-medium uppercase tracking-wide mb-4 text-amber-500">
                      Proactive
                    </h3>
                    <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-4 -mx-6 px-4">
                      <span className="text-white font-semibold text-lg block mb-3">
                        Enhanced Monitoring
                      </span>
                      {showAdminComplimentaryEnhancedOption ? (
                        <>
                          <div className="flex gap-2 justify-center max-w-xs mx-auto">
                            <div className="flex-1 min-w-0 rounded-lg bg-white/15 px-2 py-2.5 flex flex-col items-center justify-center">
                              <span className="text-white/90 text-[11px] font-medium leading-tight text-center whitespace-nowrap">
                                Complimentary
                              </span>
                              <span className="text-white text-2xl sm:text-3xl font-medium leading-none mt-1.5">
                                $0.00
                              </span>
                            </div>
                            <div className="flex-1 min-w-0 rounded-lg bg-white/15 px-2 py-2.5 flex flex-col items-center justify-center">
                              <span className="text-white/90 text-[11px] font-medium leading-tight text-center whitespace-nowrap">
                                Regular
                              </span>
                              <span className="text-white text-2xl sm:text-3xl font-medium leading-none mt-1.5">
                                $59
                                <span className="text-base sm:text-lg">
                                  .99
                                </span>
                              </span>
                            </div>
                          </div>
                          <span className="text-white/80 text-xs sm:text-sm mt-2 block">
                            PER YEAR
                          </span>
                        </>
                      ) : (
                        <>
                          <div className="text-white">
                            {isEldoradoComplimentaryEnhanced ? (
                              <span className="text-4xl font-medium">
                                $0.00
                              </span>
                            ) : (
                              <>
                                <span className="text-4xl font-medium">
                                  $59
                                </span>
                                <span className="text-xl">.99</span>
                              </>
                            )}
                          </div>
                          <span className="text-white/80 text-sm">
                            PER YEAR
                          </span>
                        </>
                      )}
                    </div>
                    {isEldoradoComplimentaryEnhanced &&
                    !isAdminDualEnhancedPricing ? (
                      <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                        {complimentaryEnhancedNote}
                      </p>
                    ) : null}
                  </div>
                  <div className="px-6 pb-6">
                    <ul className="space-y-4">
                      <li className="py-3 border-b border-border/50 text-foreground">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">
                            County Recorder Property Monitoring
                          </span>
                          <Check className="h-5 w-5 text-secondary shrink-0 ml-2" />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed text-left">
                          Continuously scans official recording data to alert
                          property owners the moment any document is filed
                          against their title.
                        </p>
                      </li>
                      <li className="py-3 border-b border-border/50 text-foreground">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">
                            Property Listing Site Monitoring
                          </span>
                          <Check className="h-5 w-5 text-secondary shrink-0 ml-2" />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed text-left">
                          Scans public real estate sites to detect suspicious
                          listings and alert you immediately.
                        </p>
                      </li>
                      <li className="py-3 border-b border-border/50 text-muted-foreground/50">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">
                            Complete Protection Property Locking
                          </span>
                          <X className="h-5 w-5 text-red-500 shrink-0 ml-2" />
                        </div>
                      </li>
                      <li className="py-3 text-muted-foreground/50">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">$1 Million Guarantee</span>
                          <X className="h-5 w-5 text-red-500 shrink-0 ml-2" />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground/70 leading-relaxed">
                          Not Included in Enhanced Monitoring
                        </p>
                      </li>
                    </ul>
                    {showAdminComplimentaryEnhancedOption ? (
                      <div className="flex flex-col gap-3 mt-6">
                        <Button
                          className="w-full bg-amber-500 hover:bg-amber-600 text-white text-sm"
                          disabled={
                            enhancedMonitoringUnavailableForProperty ||
                            complimentaryReportLoader ||
                            (paymnetLoader &&
                              getEldoradoAdminEnhancedBillingChoice() ===
                                "complimentary")
                          }
                          onClick={() =>
                            handleSelectEnhancedMonitoring("complimentary")
                          }
                        >
                          {(complimentaryReportLoader || paymnetLoader) &&
                          getEldoradoAdminEnhancedBillingChoice() ===
                            "complimentary"
                            ? "ACTIVATING…"
                            : "SELECT COMPLIMENTARY ($0.00)"}
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full border-amber-500 text-amber-700 hover:bg-amber-50 hover:text-100 text-sm hover:border-amber-500"
                          disabled={enhancedMonitoringUnavailableForProperty}
                          onClick={() =>
                            handleSelectEnhancedMonitoring("regular")
                          }
                        >
                          SELECT REGULAR ($59.99)
                        </Button>
                      </div>
                    ) : (
                      <Button
                        className="w-full mt-6 bg-amber-500 hover:bg-amber-600 text-white"
                        disabled={
                          enhancedMonitoringUnavailableForProperty ||
                          (isEldoradoComplimentaryEnhanced && paymnetLoader)
                        }
                        onClick={() => {
                          handleSelectPlan("enhanced_monitoring");
                        }}
                      >
                        {isEldoradoComplimentaryEnhanced && paymnetLoader
                          ? "ACTIVATING…"
                          : "SELECT"}
                      </Button>
                    )}
                    {(enhancedMonitoringBlockedMessage ||
                      enhancedMonitoringUnavailableForProperty) && (
                      <p className="mt-3 text-sm text-red-500 leading-relaxed text-left">
                        {enhancedMonitoringBlockedMessage ||
                          enhancedMonitoringUnavailableMessage}
                      </p>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Preventative - Complete Protection (MOST POPULAR) */}
              {shouldShowPlan("complete_protection") && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="relative bg-card rounded-2xl shadow-lg overflow-hidden md:-mt-4 md:mb-4 ring-2 ring-secondary"
                >
                  <div className="absolute top-0 left-0 right-0 bg-secondary text-white text-xs font-semibold text-center py-1.5 flex items-center justify-center gap-1.5">
                    <Award className="h-3.5 w-3.5" />
                    MOST POPULAR
                  </div>
                  <div className="p-6 pt-8 text-center">
                    <h3 className="text-xl font-medium uppercase tracking-wide mb-4 text-secondary">
                      Preventative
                    </h3>
                    <div className="bg-gradient-to-r from-secondary to-cyan-400 py-4 -mx-6">
                      <span className="text-white font-semibold text-lg block mb-2">
                        Complete Protection
                      </span>
                      <div className="text-white">
                        <span className="text-4xl font-medium">$149</span>
                        <span className="text-xl">.99</span>
                      </div>
                      <span className="text-white/80 text-sm">PER YEAR</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      $99.99 one time setup fee and state specific recording
                      deposit
                    </p>
                  </div>
                  <div className="px-6 pb-6">
                    <ul className="space-y-4">
                      <li className="py-3 border-b border-border/50 text-foreground">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">
                            County Recorder Property Monitoring
                          </span>
                          <Check className="h-5 w-5 text-secondary shrink-0 ml-2" />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed text-left">
                          Continuously scans official recording data to alert
                          property owners the moment any document is filed
                          against their title.
                        </p>
                      </li>
                      <li className="py-3 border-b border-border/50 text-foreground">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">
                            Property Listing Site Monitoring
                          </span>
                          <Check className="h-5 w-5 text-secondary shrink-0 ml-2" />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed text-left">
                          Scans public real estate sites to detect suspicious
                          listings and alert you immediately.
                        </p>
                      </li>
                      <li className="py-3 border-b border-border/50 text-foreground">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">
                            Complete Protection Property Locking
                          </span>
                          <Check className="h-5 w-5 text-secondary shrink-0 ml-2" />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed text-left">
                          "Locks" your property by filing a secured instrument
                          that blocks unauthorized transfers.
                        </p>
                      </li>
                      <li className="py-3 text-foreground bg-secondary/15 px-3 -mx-3 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">
                            $1 Million Guarantee
                          </span>
                          <Check className="h-5 w-5 text-secondary shrink-0 ml-2" />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed text-justify">
                          Reimburses eligible losses if a covered title fraud
                          event occurs despite the protection.
                        </p>
                      </li>
                    </ul>
                    <Button
                      className="w-full mt-6 bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                      onClick={() => {
                        handleSelectPlan("complete_protection");
                      }}
                    >
                      SELECT
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      </div>
      {getObjLength(basicMonitoringData) && (
        <AlertDialog
          isTailwindComponent={isTailwindComponent}
          type={basicMonitoringData?.success ? "Success" : "Error"}
          openPopup={true}
          onClose={true}
          onCloseFun={() => {
            if (basicMonitoringData?.success) {
              // Close Add Subscription / Add Property parent modal on success
              handleCloseAddPropertyModal("success");
            }
            dispatch(propertiesAction.fetchBasicMonitoringDetails({}));
          }}
          mesage={
            basicMonitoringData?.message || basicMonitoringData?.data?.message
          }
          footer={false}
        />
      )}
      {complimentaryEnrollResult ? (
        <AlertDialog
          isTailwindComponent={isTailwindComponent}
          type={complimentaryEnrollResult.success ? "Success" : "Error"}
          openPopup={true}
          onClose={true}
          onCloseFun={() => {
            const succeeded = complimentaryEnrollResult.success;
            setComplimentaryEnrollResult(null);
            dispatch(userSigupAction.fetchPaymentData({}));
            if (succeeded) {
              // Refresh /properties list and close Add Property modal.
              handleCloseAddPropertyModal("success");
            }
          }}
          mesage={complimentaryEnrollResult.message}
          footer={false}
        />
      ) : null}
      {showTotalModal && (
        <OrderSummaryModal
          open={showTotalModal}
          onOpenChange={(open) => {
            setShowTotalModal(open);
            if (!open) {
              setSelectedPlanForModal(null);
              setPromoFeedback({ message: "", isError: false });
              if (confirmingOrderSummaryRef.current) {
                confirmingOrderSummaryRef.current = false;
                return;
              }
              setCachedPaymentData(null);
              setFinalAmountRequest(null);
              clearStoredPromoCode();
              setPromoInput("");
              dispatch(userSigupAction.fetchTotalPaymentDetails({}));
            }
          }}
          title="ORDER SUMMARY"
          paymentData={
            isValidPaymentSummaryData(totalPaymentDetails?.data) &&
            totalPaymentDetails?.success
              ? totalPaymentDetails.data
              : cachedPaymentData
          }
          category={selectedCategory ?? undefined}
          totalLabel="Total due today"
          onConfirm={handleConfirmOrderSummary}
          confirmButtonText="PROCEED TO PAYMENT"
          closeButtonText="CLOSE"
          confirmDisabled={getTotalAmountLoader}
          showPromoCode
          promoCodeValue={promoInput}
          onPromoCodeChange={setPromoInput}
          onApplyPromoCode={handleApplyPromoCode}
          onRemovePromoCode={handleRemovePromoCode}
          showRemovePromoCode={hasAppliedPromoDiscount(
            isValidPaymentSummaryData(totalPaymentDetails?.data) &&
              totalPaymentDetails?.success
              ? totalPaymentDetails.data
              : cachedPaymentData,
          )}
          promoApplying={getTotalAmountLoader}
          promoMessage={promoFeedback.message}
          promoMessageIsError={promoFeedback.isError}
          onCloseClick={clearOrderSummaryPromo}
        />
      )}
      {showPaymentError && (
        <AlertDialog
          isTailwindComponent={isTailwindComponent}
          type="Error"
          openPopup={true}
          onClose={true}
          onCloseFun={closePaymentError}
          mesage={
            totalPaymentDetails?.data?.message ||
            "Failed to load payment details."
          }
        />
      )}
      {showQuestionsError && (
        <AlertDialog
          isTailwindComponent={isTailwindComponent}
          type="Error"
          openPopup={true}
          onClose={true}
          onCloseFun={closeQuestionsError}
          mesage={questionsErrorMessage}
        />
      )}

      {/* Basic Notification Disclaimer Modal */}
      <BasicNotificationDisclaimerModal
        open={showBasicDisclaimerModal}
        onOpenChange={setShowBasicDisclaimerModal}
        onContinue={() => {
          setShowBasicDisclaimerModal(false);
          handleSelectPlan("basic_monitoring");
        }}
      />

      <Loader
        open={Boolean(
          complimentaryReportLoader ||
            statesLoader ||
            basicMonitoringLoader ||
            propertyLoader ||
            pendingCustomerAddPropertyQuestions ||
            (getTotalAmountLoader && selectedPlanForModal) ||
            (paymnetLoader && isEldoradoComplimentaryEnhanced),
        )}
      />
    </div>
  );
};

export default CategoryWisePricing;
